import { useState } from 'react';

export interface PendingHarmonicCreation {
  centerPoint: number; // The center/starting point (first click)
  chartId: string; // Which chart this belongs to
  sectionKey: string; // Which section this belongs to
  type: 'harmonic' | 'sideBands'; // Whether creating harmonics or side bands
  labelPrefix: string; // Label prefix (H, C, etc.)
  color: string; // Color for the lines
  xAxisType: 'date' | 'numeric' | 'category'; // Type of x-axis
  groupId: string; // Unique group ID for this harmonic/sideBands group
}

export interface EditingHarmonicGroup {
  lineId: string;
  sectionKey: string;
  startingPoint: number;
  labelPrefix: string;
  multiplier: number;
  color: string;
  chartId: string;
  xAxisType: 'date' | 'numeric' | 'category';
  intervalType?: 'seconds' | 'minutes' | 'hours' | 'days';
  position?: { x: number; y: number };
  isNewGroup?: boolean; // True when creating new harmonics from marker, false when editing existing
}

export interface EditingSideBandsGroup {
  lineId: string;
  sectionKey: string;
  centerPosition: number; // Center position (C0)
  sidebandPosition: number; // Position of first sideband (C1)
  labelPrefix: string;
  count: number; // Number of side lines
  color: string;
  chartId: string;
  xAxisType: 'date' | 'numeric' | 'category';
  menuPosition?: { x: number; y: number };
  isNewGroup?: boolean;
}

export interface UseVerticalLineMenusReturn {
  // Harmonic menu state
  selectedHarmonicLineId: string | null;
  setSelectedHarmonicLineId: React.Dispatch<React.SetStateAction<string | null>>;
  harmonicMenuPosition: { x: number; y: number } | null;
  setHarmonicMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  // Vertical line menu state
  selectedVerticalLineId: string | null;
  setSelectedVerticalLineId: React.Dispatch<React.SetStateAction<string | null>>;
  verticalLineMenuPosition: { x: number; y: number } | null;
  setVerticalLineMenuPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  clickedChartId: string | null;
  setClickedChartId: React.Dispatch<React.SetStateAction<string | null>>;
  // Edit dialogs state
  editingHarmonicGroup: EditingHarmonicGroup | null;
  setEditingHarmonicGroup: React.Dispatch<React.SetStateAction<EditingHarmonicGroup | null>>;
  editingSideBandsGroup: EditingSideBandsGroup | null;
  setEditingSideBandsGroup: React.Dispatch<React.SetStateAction<EditingSideBandsGroup | null>>;
  // Pending creation state
  pendingHarmonicCreation: PendingHarmonicCreation | null;
  setPendingHarmonicCreation: React.Dispatch<React.SetStateAction<PendingHarmonicCreation | null>>;
}

/**
 * Manages vertical line menu state and dialog state for harmonics and side bands
 * Handles menu positioning, selected lines, and editing dialogs
 */
export function useVerticalLineMenus(): UseVerticalLineMenusReturn {
  // Track which harmonic line is selected for editing (lineId)
  const [selectedHarmonicLineId, setSelectedHarmonicLineId] = useState<string | null>(null);
  // Track menu position for the harmonic group menu
  const [harmonicMenuPosition, setHarmonicMenuPosition] = useState<{ x: number; y: number } | null>(null);
  // Track which vertical line is selected for the menu (lineId)
  const [selectedVerticalLineId, setSelectedVerticalLineId] = useState<string | null>(null);
  // Track menu position for the vertical line menu
  const [verticalLineMenuPosition, setVerticalLineMenuPosition] = useState<{ x: number; y: number } | null>(null);
  // Track which chart was clicked when opening the vertical line menu
  const [clickedChartId, setClickedChartId] = useState<string | null>(null);
  // Track which harmonic group is being edited (for showing the edit popover)
  const [editingHarmonicGroup, setEditingHarmonicGroup] = useState<EditingHarmonicGroup | null>(null);
  // Track which side bands group is being edited (for showing the side bands edit dialog)
  const [editingSideBandsGroup, setEditingSideBandsGroup] = useState<EditingSideBandsGroup | null>(null);
  // Track pending harmonic/sideBands creation (two-step process: first click = center, second click = interval)
  const [pendingHarmonicCreation, setPendingHarmonicCreation] = useState<PendingHarmonicCreation | null>(null);

  return {
    selectedHarmonicLineId,
    setSelectedHarmonicLineId,
    harmonicMenuPosition,
    setHarmonicMenuPosition,
    selectedVerticalLineId,
    setSelectedVerticalLineId,
    verticalLineMenuPosition,
    setVerticalLineMenuPosition,
    clickedChartId,
    setClickedChartId,
    editingHarmonicGroup,
    setEditingHarmonicGroup,
    editingSideBandsGroup,
    setEditingSideBandsGroup,
    pendingHarmonicCreation,
    setPendingHarmonicCreation,
  };
}
